import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { getPhoneSearchFormats, normalizePhone } from '../utils/phoneFormat';
import liff from '@line/liff';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to save session with timestamp
  const saveSession = (data) => {
    const sessionData = {
      ...data,
      _lastActivity: Date.now()
    };
    localStorage.setItem('member_profile', JSON.stringify(sessionData));
    setMember(sessionData);
  };

  // Initialize session on mount
  useEffect(() => {
    const initSession = async () => {
      try {
        const storedMember = localStorage.getItem('member_profile');
        if (storedMember) {
          const parsed = JSON.parse(storedMember);
          
          // Check expiration (2 days)
          const TWO_DAYS = 2 * 24 * 60 * 60 * 1000;
          const now = Date.now();
          const lastActivity = parsed._lastActivity || 0;
          
          if (now - lastActivity > TWO_DAYS) {
            console.log('Session expired due to inactivity');
            localStorage.removeItem('member_profile');
            setMember(null);
          } else {
            // Valid session: Update last activity to extend the session
            saveSession(parsed);
            
            // Re-validate against DB if it's a real user (not dev mock)
            if (parsed.phone && parsed.id !== '00000000-0000-0000-0000-000000000000') {
              // Run in background without blocking UI render
              loginByPhone(parsed.phone, true).catch(err => console.error('Silent refresh failed:', err));
            }
          }
        }
      } catch (err) {
        console.error('Session init error:', err);
      } finally {
        setLoading(false);
      }
    };
    initSession();
  }, []);

  const loginByPhone = async (phone, setStorage = true) => {
    try {
      setLoading(true);
      const searchFormats = getPhoneSearchFormats(phone);
      
      if (searchFormats.length === 0) {
        throw new Error('เบอร์โทรไม่ถูกต้อง');
      }

      // Query members table
      const { data, error } = await supabase
        .from('members')
        .select('*')
        .in('phone', searchFormats)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is not found
        throw error;
      }

      if (data) {
        if (setStorage) {
          saveSession(data);
        } else {
          setMember(data);
        }
        return data;
      }
      
      return null; // Not found
    } catch (err) {
      console.error('Login by phone error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginByEmail = async (email, password) => {
    try {
      setLoading(true);
      
      // Dev Bypass Logic: If it's the exact dev credentials, skip Supabase check entirely
      if (email === 'superadmin@cleanfoodcr.com' && password === 'PassWord') {
        const mockMember = {
          id: '00000000-0000-0000-0000-000000000000',
          full_name: 'ผู้ดูแลระบบ (Dev)',
          phone: '0800000000',
          member_status: 'active',
          member_type: 'admin'
        };
        
        saveSession(mockMember);
        return mockMember;
      }

      // Otherwise, attempt real Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      // If real auth succeeds, mock a profile (in production, fetch from members table)
      const memberData = {
        id: data.user.id,
        full_name: 'ผู้ใช้งาน',
        phone: '08X-XXX-XXXX',
        member_status: 'active',
        member_type: 'general'
      };
      
      saveSession(memberData);
      return memberData;
    } catch (err) {
      console.error('Login by email error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const registerMember = async (fullName, phone, lineId = null, avatarUrl = null) => {
    try {
      setLoading(true);
      const normalizedPhone = normalizePhone(phone);
      
      const newMember = {
        full_name: fullName,
        phone: normalizedPhone,
        line_id: lineId,
        avatar_url: avatarUrl,
        member_status: 'active',
        source: 'line_liff'
      };

      const { data, error } = await supabase
        .from('members')
        .insert([newMember])
        .select()
        .single();

      if (error) throw error;

      saveSession(data);
      
      // Note: Reconciling orphan orders would go here via a Supabase RPC call ideally,
      // or by executing an update statement if we have permissions to update orders.
      
      return data;
    } catch (err) {
      console.error('Register member error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyWithLine = async () => {
    try {
      setLoading(true);
      const liffId = import.meta.env.VITE_LIFF_ID;
      
      if (!liffId) {
        throw new Error('LIFF ID is missing');
      }

      if (!liff.isInClient()) {
        console.log('Not in LINE client, skipping LIFF verify');
        return null;
      }

      await liff.init({ liffId });
      
      if (!liff.isLoggedIn()) {
        liff.login();
        return null;
      }

      const profile = await liff.getProfile();
      return profile;
    } catch (err) {
      console.error('LINE verification error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setMember(null);
    localStorage.removeItem('member_profile');
    supabase.auth.signOut();
  };

  const updateMember = async (updates) => {
    if (!member?.id) return null;
    
    try {
      const { data, error } = await supabase
        .from('members')
        .update(updates)
        .eq('id', member.id)
        .select()
        .single();
        
      if (error) throw error;
      
      saveSession(data);
      return data;
    } catch (err) {
      console.error('Update member error:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{
      member,
      loading,
      loginByPhone,
      loginByEmail,
      registerMember,
      logout,
      updateMember,
      verifyWithLine
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
